import { Alert, Button, Form, Input, Select, Modal } from "antd";
import { message } from "utils/ciq-message";
import FormItem from "antd/es/form/FormItem";
import { ErrorMessages, Regex, SidepanelStyles } from "constants/index";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import { useEffect, useMemo, useState } from "react";
import {
  QUERY_USER_SUBSCRIPTION_ROLES,
  SUBSCRIPTION_LEVEL_COMPANY_LIST,
  USERS_OF_SUBSCRIPTION
} from "admin-app/services/ciq-queries";
import { MUTATION_ADMIN_INSERT_SUBSCRIPTION_USER } from "admin-app/services/mutations";
import { getGQL } from "utils/utils";
import {
  getAdminInsertUserResultMessage,
  hasAdminInsertUserFailures,
  toAdminInsertUserDisplayMessage
} from "admin-app/utils/admin-insert-user-result";
import {
  buildUserInviteMessage,
  copyTextToClipboard
} from "admin-app/utils/user-invite-message";

const { Option } = Select;

export type Role = {
  id: number;
  name: string;
};

export type CreateUserProps = {
  userData: any;
  subscriptionId: string;
  setDrawerOpen: (value: {
    showInviteModel: boolean;
    inviteUserData?: any;
  }) => void;
  showDrawerOpen: boolean;
  modelTitle: string;
  onSuccess?: () => void;
};

function CreateUser(props: CreateUserProps) {
  const {
    setDrawerOpen,
    showDrawerOpen,
    modelTitle,
    userData,
    subscriptionId,
    onSuccess
  } = props;
  const [form] = Form.useForm();
  const [responseLoading, setResponseLoading] = useState(false);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [createdInviteMessage, setCreatedInviteMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!showDrawerOpen) {
      setCreatedInviteMessage(null);
    }
  }, [showDrawerOpen]);

  const closeDrawer = () => {
    setDrawerOpen({ showInviteModel: false, inviteUserData: null });
    setCreatedInviteMessage(null);
  };

  const handleCopyInviteMessage = async () => {
    // Defensive guard: this handler is only wired to the "Copy Invite Message" button which renders only when createdInviteMessage is truthy, so this early return is unreachable.
    /* v8 ignore next */
    if (!createdInviteMessage) return;

    try {
      await copyTextToClipboard(createdInviteMessage);
      message.success("Invite message copied to clipboard.");
    } catch {
      message.error("Unable to copy invite message.");
    }
  };

  const { data: userSubscriptionRoles } = useCIQQuery(
    QUERY_USER_SUBSCRIPTION_ROLES,
    {
      fetchPolicy: "network-only"
    }
  );

  const { data: companyData, loading: companyDataLoading } = useCIQQuery(
    SUBSCRIPTION_LEVEL_COMPANY_LIST,
    {
      variables: {
        where: { subscription_id: { _eq: subscriptionId } }
      },
      fetchPolicy: "network-only",
      skip: !subscriptionId
    }
  );

  const companies = useMemo(() => {
    const subscriptionVendors = companyData?.subscription_vendors;
    if (!subscriptionVendors) return [];
    try {
      return [...subscriptionVendors].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    } catch {
      return [];
    }
  }, [companyData]);

  const hasPresetCompany = useMemo(
    () =>
      Boolean(
        userData?.company_id &&
        companies.some((c: any) => c.id === userData.company_id)
      ),
    [companies, userData?.company_id]
  );

  const hasPresetUserIdentity = useMemo(
    () =>
      Boolean(
        userData?.id ||
        userData?.email ||
        userData?.first_name ||
        userData?.last_name
      ),
    [userData]
  );

  useEffect(() => {
    if (userSubscriptionRoles) {
      const subscriptionPermission =
        userSubscriptionRoles?.subscription_permission?.filter(
          (role: any) => role.key !== "subscription_none"
        );
      setUserRoles(subscriptionPermission);
    }
  }, [userSubscriptionRoles]);

  useEffect(() => {
    if (!hasPresetUserIdentity) return;

    const userRole = userRoles.find(
      (role: any) => role?.key === "subscription_viewer"
    );
    const companyInList = companies.some(
      (c: any) => c.id === userData.company_id
    );

    form.setFieldsValue({
      first_name: userData.first_name,
      last_name: userData.last_name,
      email: userData.email,
      phone: userData.phone,
      subscription_permission_id: userRole?.id || "",
      company: companyInList ? userData.company_id : undefined
    });
  }, [form, userData, userRoles, companies, hasPresetUserIdentity]);

  const { query: usersListQuery } = getGQL(USERS_OF_SUBSCRIPTION);

  const [adminInsertSubscriptionUser] = useCIQMutation(
    MUTATION_ADMIN_INSERT_SUBSCRIPTION_USER,
    {
      refetchQueries: [
        {
          query: usersListQuery,
          variables: { subscription_id: subscriptionId }
        }
      ]
    }
  );

  const onSendInvite = async (values: any) => {
    setResponseLoading(true);
    try {
      const response = await adminInsertSubscriptionUser({
        variables: {
          company_id: values.company,
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
          subscription_id: subscriptionId,
          phone: values.phone ?? "",
          subscription_permission_id: values.subscription_permission_id
        }
      });

      if (!response.success) {
        message.error(
          toAdminInsertUserDisplayMessage(
            response.error?.message ?? response.errorMsg,
            "Failed to create user."
          )
        );
        return;
      }

      const result = response.data?.admin_insert_subscription_user;

      if (hasAdminInsertUserFailures(result?.failures)) {
        message.error(
          getAdminInsertUserResultMessage(result, "Failed to create user.")
        );
        return;
      }

      const successMessage = getAdminInsertUserResultMessage(result, "");
      if (successMessage) {
        message.success(successMessage);
        setCreatedInviteMessage(
          buildUserInviteMessage("subscription", {
            firstName: values.first_name,
            email: values.email,
            password: values.password
          })
        );
        onSuccess?.();
      } else {
        message.error("Failed to create user.");
      }
    } catch {
      message.error("An error occurred while creating user.");
    } finally {
      setResponseLoading(false);
    }
  };

  return (
    <Modal
      className="custom-drawer"
      title={modelTitle}
      width={420}
      style={SidepanelStyles.style}
      footer={
        <div className="flex justify-end gap-2">
          {createdInviteMessage && (
            <Button
              onClick={handleCopyInviteMessage}
              data-testid="copy-invite-message-btn"
            >
              Copy Draft Email
            </Button>
          )}
          {createdInviteMessage ? (
            <Button
              type="primary"
              onClick={closeDrawer}
              data-testid="close-create-user-btn"
            >
              Close
            </Button>
          ) : (
            <Button
              form="create-user-form"
              htmlType="submit"
              disabled={responseLoading || !companies.length}
              loading={responseLoading}
              type="primary"
              data-testid="send-invite-btn"
            >
              Create User
            </Button>
          )}
        </div>
      }
      open={showDrawerOpen}
      onCancel={closeDrawer}
      destroyOnHidden
    >
      <div className="flex-col h-full overflow-y overflow-x-hidden px-3">
        {createdInviteMessage && (
          <Alert
            type="success"
            showIcon
            className="mb-3"
            data-testid="create-user-success-alert"
            // antd v6 renamed alert internals (ant-alert-message -> ant-alert-title), so
            // wrap the message in a stable data-testid hook for class-independent automation
            title={
              <span data-testid="create-user-success-alert-message">
                User created. Copy the draft email to send to the user.
              </span>
            }
          />
        )}
        <Form
          id="create-user-form"
          preserve={false}
          onFinish={onSendInvite}
          form={form}
          scrollToFirstError
          layout="vertical"
          disabled={Boolean(createdInviteMessage)}
        >
          <div>
            <FormItem
              name="company"
              label="Company"
              rules={[
                {
                  required: true,
                  validateTrigger: "onSubmit",
                  message: ErrorMessages.CompanySelectionRequired,
                  validator(_, value: string) {
                    if (!value) {
                      return Promise.reject(new Error());
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Select
                loading={companyDataLoading}
                disabled={
                  companyDataLoading ||
                  hasPresetCompany ||
                  Boolean(createdInviteMessage)
                }
                showSearch
                filterOption={(input, option: any) => {
                  const cmpny: any = companies.find(
                    (c: any) => c.id === option.key
                  );

                  if (!cmpny) return false;
                  const inputMes = input.toString().toLowerCase();
                  const companyDt = [cmpny?.name?.toLowerCase()];
                  return companyDt?.some((text) => text.includes(inputMes));
                }}
                data-testid="company-select"
              >
                {companies.map((company: any) => (
                  <Option
                    data-testid={`company-options-${company.id}`}
                    key={company.id}
                    value={company.id}
                  >
                    {company.name}
                  </Option>
                ))}
              </Select>
            </FormItem>
            <FormItem
              name="first_name"
              label="First Name"
              rules={
                hasPresetUserIdentity
                  ? undefined
                  : [
                      {
                        required: true,
                        validateTrigger: "onSubmit",
                        message: ErrorMessages.FirstName
                      }
                    ]
              }
            >
              <Input
                disabled={hasPresetUserIdentity}
                data-testid={
                  hasPresetUserIdentity
                    ? "input-disable-firstName"
                    : "input-firstName"
                }
              />
            </FormItem>
            <FormItem
              name="last_name"
              label="Last Name"
              rules={
                hasPresetUserIdentity
                  ? undefined
                  : [
                      {
                        required: true,
                        validateTrigger: "onSubmit",
                        message: ErrorMessages.LastName
                      }
                    ]
              }
            >
              <Input
                disabled={hasPresetUserIdentity}
                data-testid={
                  hasPresetUserIdentity
                    ? "input-disable-lastName"
                    : "input-lastName"
                }
              />
            </FormItem>
            <FormItem
              name="email"
              label="Email"
              rules={
                hasPresetUserIdentity
                  ? undefined
                  : [
                      {
                        required: true,
                        validateTrigger: "onSubmit",
                        message: ErrorMessages.EmailRequired
                      },
                      {
                        type: "email",
                        validateTrigger: "onSubmit",
                        message: ErrorMessages.EmailInvalid
                      }
                    ]
              }
            >
              <Input
                disabled={hasPresetUserIdentity}
                data-testid={
                  hasPresetUserIdentity ? "input-disable-email" : "input-email"
                }
              />
            </FormItem>

            <FormItem name="phone" label="Phone Number">
              <Input data-testid="input-phone" />
            </FormItem>
            <FormItem
              name="subscription_permission_id"
              label="Permission"
              rules={[
                {
                  required: true,
                  validateTrigger: "onSubmit",
                  message: ErrorMessages.SubscriptionRole,
                  validator(_, value: string) {
                    if (!value) {
                      return Promise.reject(new Error());
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Select style={{ width: "100%" }} data-testid="permission-select">
                {userRoles?.length > 0 &&
                  userRoles?.map((role: Role) => (
                    <Option key={role.id} value={role.id}>
                      {role.name}
                    </Option>
                  ))}
              </Select>
            </FormItem>
            <FormItem
              name="password"
              label="Password"
              rules={[
                {
                  validateTrigger: "onChange",
                  required: true,
                  pattern: Regex.password,
                  message: ErrorMessages.PasswordRequired
                }
              ]}
              hasFeedback
            >
              <Input.Password placeholder="Enter Password" />
            </FormItem>
            <FormItem
              name="confirm_password"
              label="Confirm Password"
              dependencies={["password"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  validateTrigger: "onChange",
                  message: ErrorMessages.ConfirmPasswordRequired
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(ErrorMessages.ConfirmPasswordRequired)
                    );
                  }
                })
              ]}
            >
              <Input.Password placeholder="Confirm Password" />
            </FormItem>
          </div>
        </Form>
      </div>
    </Modal>
  );
}

export default CreateUser;
